import { useCallback, useEffect } from 'react';
import { useLocation, Location } from 'react-router-dom';
import { useRecoilState } from 'recoil';

import { globalState } from '@/store/global';
import settings from '@/config/settings';

import { IRouter, IPathKeyRouter, TabNavItem } from '@/@types/router';

/**
 * 负责维护 headTabNavList（标签页列表）。
 *
 * 该逻辑原本耦合在 RightTabNav 组件里，但页面内容（UniversalLayout）的渲染
 * 依赖 headTabNavList —— 因此必须把它解耦出来，无论 tab 导航 UI 是否渲染，
 * 当前路由都要被正确加入 headTabNavList，否则页面会显示空白。
 */
const useTabNavSync = (jsonMenuData: IPathKeyRouter) => {
  const location = useLocation();
  const [global, setGlobal] = useRecoilState(globalState);

  const tabNavList = global.headTabNavList;

  const setGlobalHeadTabNavList = useCallback(
    (val: (currVal: typeof global) => TabNavItem[]) => {
      setGlobal((preVal) => ({ ...preVal, headTabNavList: [...val(preVal)] }));
    },
    [setGlobal],
  );

  const setTabNav = useCallback(
    (currentLocation: Location, currentRouteItem: IRouter): void => {
      // 标签页按页面路径唯一；参数变化更新该标签的地址。
      if (
        !currentRouteItem ||
        (currentRouteItem.children && currentRouteItem.children?.length > 0) ||
        !currentRouteItem.component
      ) {
        return;
      }
      setGlobalHeadTabNavList((val) => {
        const index = val.headTabNavList.findIndex((item) => item.location.pathname === currentLocation.pathname);
        const next = { location: currentLocation, menu: { ...currentRouteItem } };
        if (index < 0) {
          return currentLocation.pathname === settings.tabNavHomePath
            ? [next, ...val.headTabNavList] : [...val.headTabNavList, next];
        }
        return val.headTabNavList.map((item, i) => i === index ? next : item);
      });
    },
    [setGlobalHeadTabNavList],
  );

  // 负责“添加当前路由到标签页”的 Effect
  useEffect(() => {
    const routeItem = jsonMenuData[location.pathname];
    if (!routeItem || location.pathname !== routeItem.path) {
      return;
    }
    setTabNav(location, routeItem);
  }, [location, jsonMenuData, setTabNav]);

  // 负责“初始化首页标签页”的 Effect
  useEffect(() => {
    const homeRouter = jsonMenuData[settings.tabNavHomePath];
    if (!homeRouter) {
      return;
    }
    const isHomeExist = tabNavList.some((item) => item.menu.path === settings.tabNavHomePath);
    if (!isHomeExist) {
      setTabNav({ pathname: settings.tabNavHomePath, hash: '', search: '', state: null, key: '' }, homeRouter);
    }
  }, [tabNavList, jsonMenuData, setTabNav]);
};

export default useTabNavSync;
